import React from "react";
import TermsAndConditionsField from "../../common/TermsAndConditionsField";
import AgreementField from "../AgreementField";
import CheckAgreementField from "../CheckAgreementField";
import DocumentAgreement from "../DocumentAgreement/DocumentAgreement";
import EmailField from "../EmailField";
import InfoField from "../InfoField";
import TextField from "../TextField";
import { getValidationsObject, getDateValue } from "../validationUtils";
import RadioField from "../RadioField";
import DropdownField from "../DropdownField";
import ParagraphField from "../ParagraphField";
import type { Field, FieldType, StandardFieldProps, CustomRegisterFieldProps, Condition } from "../types";
import { useFormContext } from "react-hook-form";
import { evaluateCondition } from "../../core/utils";

// Props for DynamicField
export interface DynamicFieldProps {
  field: Field;
}

const DynamicField: React.FC<DynamicFieldProps> = (props) => {
  const form = useFormContext();
  const { field } = props;
  const { formState: { errors }, getValues, watch } = form;

  const shouldCustomRegister = (type: FieldType): boolean => {
    switch (type) {
      case "documentAgreement":
        return true;
      default:
        return false;
    }
  };

  const fieldProps = (field: DynamicFieldProps["field"]): StandardFieldProps | CustomRegisterFieldProps => {
    const validationsObject = getValidationsObject(field, getValues);
    if (shouldCustomRegister(field.type)) {
      return {
        field: field,
        validationsObject: validationsObject,
      };
    } else {
      let dateValue: Date | string | undefined = undefined;

      // Format date value if field type is date
      if (field.dataType === "date") {
        dateValue = getDateValue(field.value as string | number | Date);
      }

      return {
        name: field.name,
        value: field.value,
        placeholder: field.label,
        dateValue: dateValue,
        dataType: field.dataType,
        format: field.format,
        field: field,
        error: errors[field.name],
        validationsObject: validationsObject,
      };
    }
  };

  if (field.displayCondition) {
    const conditionMet = evaluateCondition(field.displayCondition, (fieldName) => watch(fieldName));

    if (!conditionMet) {
      return <React.Fragment />;
    }
  }

  switch (field.type) {
    case "string":
    case "phoneNumber":
      return <TextField {...(fieldProps(field) as StandardFieldProps)} />;
    case "email":
      return <EmailField {...(fieldProps(field) as StandardFieldProps)} />;
    case "number":
      return <TextField type="number" {...(fieldProps(field) as StandardFieldProps)} />;
    case "info":
      return <InfoField {...(fieldProps(field) as StandardFieldProps)} />;
    case "agreement":
      return <AgreementField {...(fieldProps(field) as StandardFieldProps)} />;
    case "checkAgreement":
      return <CheckAgreementField {...(fieldProps(field) as StandardFieldProps)} />;
    case "documentAgreement":
      return <DocumentAgreement {...(fieldProps(field) as CustomRegisterFieldProps)} />;
    case "termsAndConditionsAgreement":
      return <TermsAndConditionsField {...(fieldProps(field) as StandardFieldProps)} />;
    case "password":
      return <TextField type="password" {...(fieldProps(field) as StandardFieldProps)} />;
    case "radio":
      return <RadioField {...(fieldProps(field) as StandardFieldProps)} />;
    case "dropdown":
      return <DropdownField {...(fieldProps(field) as StandardFieldProps)} />;
    case "paragraph":
      return <ParagraphField {...(fieldProps(field) as StandardFieldProps)} />;
    default:
      return <React.Fragment />;
  }


};

export default DynamicField;
